import {
  Component, OnInit, ChangeDetectorRef, ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import * as _ from 'lodash';

// data, models, helpers
import { customFieldsSegmentData } from '../../../product-details-v2/segment-data/custom-fields-segment-data';
import { CustomField } from '../../../product-details-v2/models/custom-field.model';
import { populateModel } from 'utils/utils';
import { SegmentFieldItem } from '../../../product-details-v2/segment-data/segment-field-item.interface';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import { CollapseGroupComponent } from 'shared/collapse/collapse-group/collapse-group.component';

// services
import { ProductPanelService } from '../../product-panel.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { BrandService } from 'shared/brand/brand.service';
import { SearchService } from 'shared/search/search.service';


@Component({
  moduleId: module.id,
  selector: 'custom-fields-segment',
  templateUrl: 'custom-fields.component.html',
  styleUrls: ['custom-fields.component.scss'],
})
export class CustomFieldsComponent extends BaseSegmentComponent implements OnInit {

  @ViewChild(TableEditableComponent, {static: false})
  tableEditable: TableEditableComponent | void;

  customFieldsForm: UntypedFormGroup;
  pathToData: string = 'customFields';
  segmentName: string = 'custom-fields';
  segmentData: any = customFieldsSegmentData;
  declare itemDetailsData: any;

  get formArray(): UntypedFormArray {
    return this.segmentForm.get('customFields') as UntypedFormArray;
  }

  get segmentForm(): any {
    return this.customFieldsForm;
  }

  constructor(
    protected productPanelService: ProductPanelService,
    protected route: ActivatedRoute,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected ref: ChangeDetectorRef,
    private fb: UntypedFormBuilder
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.customFieldsForm = this.fb.group({
      customFields: this.fb.array([])
    });
    this.subscribeToItemDetail();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onAddCustomFieldClick() {
    this.addFormGroupItem({label: '', value: ''});
    this.formArray.markAsDirty();
    this.ref.markForCheck();
    _.defer(() => this.tableEditable && this.tableEditable.toggleLastCollapse());
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.setData(itemDetail[this.pathToData]);
  }

  setData(customFields: Array<any>): void {
    this.itemDetailsData = customFields;
    this.updateAttributesForm();
  }

  setBulkData(): void {
    this.setData(this.getBulkSegmentData(this.segmentName));
  }

  save(): any {
    if (this.bulkEdit) {
      return this.putDataBulk(
        this.segmentName,
        this.formArray.value,
      );
    } else {
      return this.putData(this.itemId, this.segmentName, this.formArray.value);
    }
  }

  isTextField(segmentItemName: string): boolean {
    return segmentItemName !== 'type_id';
  }

  getPreview(item: SegmentFieldItem, formGroup: UntypedFormGroup): string {
    const value = formGroup.get(item.field_map).value;
    if (item.lookup) {
      return _.find(item.lookup, { option: value })?.title.toString();
    }
    return value;
  }

  getItemTitle(item: SegmentFieldItem, formGroup?: UntypedFormGroup) {
    switch (item.field_map) {
      case 'type_id':
        return 'PIES Segment';
      case 'label':
        const rowTypeId = formGroup && formGroup.get('type_id')?.value;
        return this.getCustomFieldLabel(rowTypeId);
      case 'value':
        return 'Value';
    }
  }

  public getCustomFieldLabel(rowTypeId: number): string {
    switch (rowTypeId) {
      case 1:
        return 'Custom Field';
      case 2:
        return 'Price Type';
      case 3:
        return 'Description Type';
      default:
        return 'Custom Field';
    }
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private updateAttributesForm(): void {
    // create formArray
    this.segmentForm.setControl('customFields', this.fb.array([]));
    _.each(this.itemDetailsData, (item: any) => {
      this.addFormGroupItem(item);
    });
    this.markAsPristine();
    this.ref.markForCheck();
  }

  private addFormGroupItem(itemData: any): void {
    const model = populateModel(CustomField, customFieldsSegmentData, itemData);
    this.formArray.push(this.fb.group(model));
  }
}
